import { useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import { formatMontant, formatMontantFCFA, parseMontant } from "@/lib/formatMontant";
import { InputMontant } from "@/components/ui/InputMontant";
import { generateUUID } from "@/lib/utils";

export type GrilleSaisieRow = {
  id: string;
  date: string;
  libelle: string;
  debit: string;
  credit: string;
};

export function emptyGrilleRow(): GrilleSaisieRow {
  return { id: generateUUID(), date: "", libelle: "", debit: "", credit: "" };
}

export function parseMontantCell(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = parseMontant(t);
  if (n < 0) return null;
  return n;
}

export function rowHasError(r: GrilleSaisieRow): boolean {
  const d = parseMontantCell(r.debit);
  const c = parseMontantCell(r.credit);
  return d != null && c != null && d > 0 && c > 0;
}

export function rowIsSubstantiallyEmpty(r: GrilleSaisieRow): boolean {
  return !r.date.trim() && !r.libelle.trim() && !r.debit.trim() && !r.credit.trim();
}

type GrilleSaisieOperationsProps = {
  rows: GrilleSaisieRow[];
  setRows: Dispatch<SetStateAction<GrilleSaisieRow[]>>;
};

const COLS = 4 as const;

export default function GrilleSaisieOperations({ rows, setRows }: GrilleSaisieOperationsProps) {
  const wrapRef = useRef<HTMLDivElement>(null);

  const focusCell = (r: number, c: number) => {
    requestAnimationFrame(() => {
      const el = wrapRef.current?.querySelector<HTMLInputElement>(`[data-sr="${r}-${c}"]`);
      el?.focus();
      el?.select?.();
    });
  };

  const onKeyDown = (e: React.KeyboardEvent, r: number, c: number) => {
    if (e.key === "Escape") {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      let nc = c + (e.shiftKey ? -1 : 1);
      let nr = r;
      if (nc >= COLS) {
        nc = 0;
        nr += 1;
      }
      if (nc < 0) {
        nc = COLS - 1;
        nr -= 1;
      }
      if (nr >= rows.length) {
        setRows((prev) => {
          if (prev.length === 0) {
            const n = [emptyGrilleRow()];
            requestAnimationFrame(() => focusCell(0, 0));
            return n;
          }
          const last = prev[prev.length - 1];
          if (rowIsSubstantiallyEmpty(last)) {
            requestAnimationFrame(() => focusCell(prev.length - 1, 0));
            return prev;
          }
          const n = [...prev, emptyGrilleRow()];
          requestAnimationFrame(() => focusCell(n.length - 1, 0));
          return n;
        });
        return;
      }
      if (nr < 0) nr = 0;
      focusCell(nr, nc);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const nr = r + 1;
      if (nr >= rows.length) {
        setRows((prev) => {
          if (prev.length === 0) {
            const n = [emptyGrilleRow()];
            requestAnimationFrame(() => focusCell(0, 0));
            return n;
          }
          const last = prev[prev.length - 1];
          if (rowIsSubstantiallyEmpty(last)) {
            requestAnimationFrame(() => focusCell(prev.length - 1, 0));
            return prev;
          }
          const n = [...prev, emptyGrilleRow()];
          requestAnimationFrame(() => focusCell(n.length - 1, 0));
          return n;
        });
        return;
      }
      focusCell(nr, 0);
    }
  };

  const onBlurLastRow = (r: number) => {
    if (r !== rows.length - 1) return;
    setRows((prev) => {
      const last = prev[prev.length - 1];
      if (rowIsSubstantiallyEmpty(last)) return prev;
      return [...prev, emptyGrilleRow()];
    });
  };

  useEffect(() => {
    if (rows.length === 0) setRows([emptyGrilleRow()]);
  }, [rows.length, setRows]);

  let totalDebit = 0;
  let totalCredit = 0;
  for (const row of rows) {
    const d = parseMontantCell(row.debit);
    const c = parseMontantCell(row.credit);
    if (d != null) totalDebit += d;
    if (c != null) totalCredit += c;
  }

  return (
    <div ref={wrapRef} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="grid grid-cols-[140px_1fr_120px_120px] gap-px bg-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <div className="bg-slate-50 px-3 py-2">Date</div>
        <div className="bg-slate-50 px-3 py-2">Libellé</div>
        <div className="bg-slate-50 px-3 py-2 text-right">Débit</div>
        <div className="bg-slate-50 px-3 py-2 text-right">Crédit</div>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((row, r) => {
          const err = rowHasError(row);
          const dateInvalid =
            row.date.trim() !== "" &&
            !/^\d{4}-\d{2}-\d{2}$/.test(row.date.trim()) &&
            Number.isNaN(Date.parse(row.date));
          return (
            <div
              key={row.id}
              className={`grid grid-cols-[140px_1fr_120px_120px] gap-px bg-slate-100 ${err ? "ring-1 ring-amber-400 ring-inset" : ""}`}
            >
              <input
                data-sr={`${r}-0`}
                type="date"
                value={row.date}
                onChange={(e) =>
                  setRows((prev) => prev.map((x, i) => (i === r ? { ...x, date: e.target.value } : x)))
                }
                onKeyDown={(e) => onKeyDown(e, r, 0)}
                onBlur={() => onBlurLastRow(r)}
                className={`border-0 bg-white px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-inset focus:ring-[#1a2744]/20 focus:border-[#1a2744] ${
                  dateInvalid ? "bg-amber-50" : ""
                }`}
              />
              <input
                data-sr={`${r}-1`}
                type="text"
                placeholder="Libellé"
                value={row.libelle}
                onChange={(e) =>
                  setRows((prev) => prev.map((x, i) => (i === r ? { ...x, libelle: e.target.value } : x)))
                }
                onKeyDown={(e) => onKeyDown(e, r, 1)}
                onBlur={() => onBlurLastRow(r)}
                className="border-0 bg-white px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-inset focus:ring-[#1a2744]/20 focus:border-[#1a2744]"
              />
              <div className="border-0 bg-white focus-within:ring-2 focus-within:ring-inset focus-within:ring-orange-400">
                <InputMontant
                  data-sr={`${r}-2`}
                  placeholder="0"
                  suffix=""
                  emptyWhenZero={row.debit.trim() === ""}
                  value={parseMontant(row.debit)}
                  onChange={(n) => {
                    const debitStr = n === 0 ? "" : formatMontant(n);
                    setRows((prev) =>
                      prev.map((x, i) =>
                        i === r ? { ...x, debit: debitStr, credit: n !== 0 ? "" : x.credit } : x
                      )
                    );
                  }}
                  onKeyDown={(e) => onKeyDown(e, r, 2)}
                  onBlur={() => onBlurLastRow(r)}
                  className="border-0 bg-transparent px-2 py-2 text-sm font-mono tabular-nums outline-none"
                />
              </div>
              <div className="border-0 bg-white focus-within:ring-2 focus-within:ring-inset focus-within:ring-orange-400">
                <InputMontant
                  data-sr={`${r}-3`}
                  placeholder="0"
                  suffix=""
                  emptyWhenZero={row.credit.trim() === ""}
                  value={parseMontant(row.credit)}
                  onChange={(n) => {
                    const creditStr = n === 0 ? "" : formatMontant(n);
                    setRows((prev) =>
                      prev.map((x, i) =>
                        i === r ? { ...x, credit: creditStr, debit: n !== 0 ? "" : x.debit } : x
                      )
                    );
                  }}
                  onKeyDown={(e) => onKeyDown(e, r, 3)}
                  onBlur={() => onBlurLastRow(r)}
                  className="border-0 bg-transparent px-2 py-2 text-sm font-mono tabular-nums outline-none"
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-[140px_1fr_120px_120px] gap-px border-t border-slate-200 bg-slate-50 text-sm font-semibold">
        <div className="col-span-2 px-3 py-3 text-slate-600">Totaux</div>
        <div className="px-2 py-3 text-right font-mono tabular-nums text-slate-900">
          {formatMontantFCFA(totalDebit)}
        </div>
        <div className="px-2 py-3 text-right font-mono tabular-nums text-slate-900">
          {formatMontantFCFA(totalCredit)}
        </div>
      </div>
      <div className="border-t border-slate-200 bg-slate-50 px-3 py-2 text-right text-sm text-slate-600">
        Solde (crédits − débits) :{" "}
        <span className="font-mono font-semibold text-slate-900">
          {formatMontantFCFA(totalCredit - totalDebit)}
        </span>
      </div>
      {rows.some(rowHasError) ? (
        <p className="border-t border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Certaines lignes ont à la fois un débit et un crédit : corrigez avant enregistrement.
        </p>
      ) : null}
    </div>
  );
}
